import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useAgentDialog, type AgentFinish } from '@/modules/use-agent-dialog';
import { collectAgentPageContext } from '@/modules/personal-agent-api';
import { useNow } from '@/modules/use-now';
import { useIsMobile, useWindowWidth } from '@/modules/use-window-width';
import { zAiSheet } from '@/shared/constants/zIdxs';
import AgentThread from '@/shared/ui/AgentThread';
import AgentHistory from '@/shared/ui/AgentHistory';
import AgentPanelActions from '@/shared/ui/AgentPanelActions';
import AgentEditingBar from '@/shared/ui/AgentEditingBar';
import AgentEditConfirmModal from '@/shared/ui/AgentEditConfirmModal';
import CoinIcon from '@/shared/ui/CoinIcon';
import SendOrStopButton from '@/shared/ui/SendOrStopButton';
import {
  ACTIVITY_AI_QUESTIONS,
  MARKET_AI_QUESTIONS,
  POPULAR_AI_QUESTIONS,
} from '@/shared/utils/popularAiQuestions';
import { marketPageContext } from '@/modules/market-page-context';
import { track } from '@/shared/utils/track';
import type { AiScope } from '@/shared/utils/useAiOpen';
import { useAgentComposer } from '@/shared/utils/useAgentComposer';

const CHIP_CLASS =
  'btn-reset rounded-2xl bg-background-main px-l py-s text-left text-body-md font-medium text-gray-text transition-colors ease-[ease] hover:text-purple-default';
// The design shows a four-line draft and no maximum; past this many lines
// the box scrolls inside instead of growing.
const MAX_DRAFT_LINES = 6;
// SendOrStopButton's size-8. The single-row layout gives the text the row
// minus the button, the two-row layout the full row, and the layout decision
// is always measured at the single-row width — a draft that wraps only at
// the narrower width would otherwise flip the layout back and forth on every
// measurement.
const SEND_BUTTON_WIDTH = 32;

// Stays mounted while the widget is on screen (the parent toggles
// visibility), so closing neither aborts an in-flight answer nor discards
// the shown conversation. Sending an edited message asks for confirmation
// because the backend deletes every later message.
export default function Panel({
  open,
  scope,
  onClose,
}: {
  open: boolean;
  scope: AiScope;
  onClose: () => void;
}) {
  const dialog = useAgentDialog({ onFinish });
  const { messages, busy, failure, submit, reply, stop, retryLoad } = dialog;
  const width = useWindowWidth();
  const isMobile = useIsMobile();
  const [multiline, setMultiline] = useState(false);
  const [historyList, setHistoryList] = useState<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const {
    view,
    query,
    setQuery,
    editing,
    canScrollDown,
    scrollRef,
    askQuestion,
    submitComposer,
    startEditing,
    cancelEditing,
    keepEditing,
    confirmEdit,
    openHistory,
    showChat,
    openHistoryDialog,
    onThreadScroll,
  } = useAgentComposer<'popular_question' | 'suggested_question'>({
    open,
    onClose,
    dialog,
    surface: 'widget',
    send: (question, source) => {
      track('ai_widget_question_asked', {
        source,
        follow_up: messages.length > 0,
      });
      submit(
        question,
        scope === null ?
          collectAgentPageContext(document.getElementById('page-content'))
        : scope.kind === 'market' ? marketPageContext(scope.signal)
        : scope.pageContext,
      );
    },
    focusComposer: () => {
      (textareaRef.current ?? inputRef.current)?.focus();
    },
  });

  useEffect(() => {
    // Not on ≤767: the raised keyboard would cover the sheet's chips.
    if (open && !isMobile && view === 'chat') inputRef.current?.focus();
  }, [open, isMobile, view]);

  // `overflow: hidden` on body does not stop iOS touch scrolling —
  // body-scroll-lock does (the Modal precedent), and only scrolling inside
  // its target survives, so the target must be whichever element actually
  // scrolls; with none (History still loading, or failed) the sheet itself
  // keeps the page locked.
  useLayoutEffect(() => {
    if (!open || !isMobile) return undefined;
    const scroller =
      (view === 'chat' ? scrollRef.current : historyList) ?? dialogRef.current;
    if (scroller === null) return undefined;
    disableBodyScroll(scroller, { reserveScrollBarGap: true });
    return () => {
      enableBodyScroll(scroller);
    };
  }, [open, isMobile, view, historyList, scrollRef]);

  // `open` and `width` are dependencies the effect never reads: a hidden
  // box measures 0, and a viewport width change reflows the lines.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (el === null) return;
    el.style.height = '';
    const oneLine = el.clientHeight;
    if (oneLine === 0) return;
    const linesAt = (probeWidth: string): number => {
      el.style.width = probeWidth;
      const lines = Math.round(el.scrollHeight / oneLine);
      el.style.width = '';
      return lines;
    };
    const singleRowLines =
      multiline ?
        linesAt(`${el.clientWidth - SEND_BUTTON_WIDTH}px`)
      : linesAt('');
    const lines = multiline ? linesAt('') : singleRowLines;
    el.style.height = `${Math.min(lines, MAX_DRAFT_LINES) * oneLine}px`;
    el.style.overflowY = lines > MAX_DRAFT_LINES ? 'auto' : 'hidden';
    setMultiline(singleRowLines > 1);
  }, [query, multiline, open, width]);

  const timestampsShown = open && (view === 'history' || messages.length > 0);
  const now = useNow(30_000, timestampsShown);

  const idle = messages.length === 0 && failure === null && busy === null;

  const actions = (
    <AgentPanelActions onHistory={openHistory} onClose={onClose} />
  );

  const pair =
    scope === null ? null : `${scope.signal.asset}/${scope.signal.quoteAsset}`;
  const chips =
    scope === null ? POPULAR_AI_QUESTIONS
    : scope.kind === 'market' ? MARKET_AI_QUESTIONS
    : ACTIVITY_AI_QUESTIONS;

  const header =
    scope === null ?
      <div className="flex w-full shrink-0 items-center justify-between">
        <p className="text-body-lg font-bold text-gray-text">
          👋 Hi, how can I help?
        </p>
        {actions}
      </div>
    : scope.kind === 'activity' ?
      <div className="flex w-full shrink-0 items-start justify-between gap-l">
        <div className="flex min-w-0 flex-1 flex-col">
          <p className="text-body-lg font-bold text-gray-text">
            AI Market Analyst
          </p>
          <p className="text-body-md font-medium text-gray">
            Ready to answer questions about the {pair} snapshot
          </p>
        </div>
        {actions}
      </div>
    : <div className="flex w-full shrink-0 items-start justify-between">
        <div className="flex min-w-0 flex-1 flex-col items-start justify-center">
          <div className="flex max-w-full items-center gap-s">
            <CoinIcon
              asset={scope.signal.asset}
              slug={scope.signal.slug}
              logoUrl={scope.signal.logoUrl}
              size={24}
            />
            <p className="truncate text-body-lg font-bold text-gray-text">
              {scope.signal.asset}/{scope.signal.quoteAsset}
            </p>
          </div>
          <div className="flex w-full items-center gap-s">
            <p className="min-w-px flex-1 truncate text-body-sm font-medium text-gray">
              based on @{scope.signal.kol.handle}’s call
            </p>
          </div>
        </div>
        {actions}
      </div>;

  const sendOrStop = (
    <SendOrStopButton
      streaming={busy === 'streaming'}
      onStop={stop}
      sendDisabled={query.trim() === '' || busy !== null}
      stopLabel="Stop generating"
      sendLabel="Send"
    />
  );

  const form =
    isMobile ?
      // A textarea with no Enter handler: on the sheet only the button
      // sends, and the mobile keyboard's return key inserts a newline.
      <form
        className={`flex flex-col items-end rounded-2xl bg-background-main ${
          multiline ? 'px-s pt-m pb-s' : 'p-s'
        }`}
        onSubmit={(e) => {
          e.preventDefault();
          submitComposer();
        }}
      >
        <div
          className={`flex w-full ${
            multiline ? 'flex-col gap-s' : 'items-center justify-between'
          }`}
        >
          <div
            className={`flex px-m ${
              multiline ? 'w-full items-start' : 'min-w-0 flex-1 items-center'
            }`}
          >
            <textarea
              ref={textareaRef}
              rows={1}
              className="w-full resize-none text-body-md font-medium text-gray-text outline-none placeholder:text-gray-2"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask AI"
              aria-label="Ask AI"
              autoComplete="off"
              disabled={busy !== null}
            />
          </div>
          <div
            className={`flex ${
              multiline ? 'w-full items-end justify-end' : 'items-center'
            }`}
          >
            {sendOrStop}
          </div>
        </div>
      </form>
    : <form
        className="flex h-12 items-center gap-s rounded-full bg-background-main py-s pr-s pl-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          submitComposer();
        }}
      >
        <input
          ref={inputRef}
          type="text"
          className="min-w-0 flex-1 text-body-lg font-medium text-gray-text outline-none placeholder:text-gray-2"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask AI"
          aria-label="Ask AI"
          autoComplete="off"
          disabled={busy !== null}
        />
        {sendOrStop}
      </form>;

  return (
    <>
      {isMobile && (
        <div
          className="fixed inset-0 bg-black/10"
          style={{ zIndex: zAiSheet }}
          onClick={onClose}
        />
      )}
      <div
        ref={dialogRef}
        role="dialog"
        aria-label="AI assistant"
        className={
          isMobile ?
            // top-12 = the ≤767 top bar's 48px (Header: py-s around its
            // 32px controls).
            'fixed inset-x-0 top-12 bottom-0 flex flex-col gap-2xl rounded-t-xl bg-background-secondary px-2xl pt-2xl drop-shadow-[0px_-2px_2px_rgba(110,119,141,0.2)]'
          : 'fixed right-10 bottom-32 z-260 flex h-155 max-h-[calc(100svh-9rem)] w-100 flex-col gap-2xl rounded-md bg-background-secondary p-2xl shadow-[0px_0px_15px_rgba(0,0,0,0.1)]'
        }
        style={
          isMobile ?
            {
              zIndex: zAiSheet,
              paddingBottom: 'max(40px, env(safe-area-inset-bottom) + 8px)',
            }
          : undefined
        }
      >
        {view === 'history' ?
          <AgentHistory
            listRef={setHistoryList}
            now={now}
            onBack={showChat}
            onClose={onClose}
            onOpenDialog={openHistoryDialog}
          />
        : <>
            {header}
            <div className="relative min-h-0 flex-1">
              <div
                ref={scrollRef}
                onScroll={onThreadScroll}
                className="flex h-full flex-col overflow-y-auto"
              >
                {idle ?
                  <>
                    {scope?.kind === 'market' && (
                      <div className="flex w-full flex-col items-start gap-xs text-body-lg">
                        <p className="w-full font-bold text-gray-text">
                          Analyze {scope.signal.asset} with AI
                        </p>
                        <p className="w-full font-medium text-gray">
                          Explore the market with AI and save useful thoughts to
                          your journal.
                        </p>
                      </div>
                    )}
                    {/* The sheet's draft state hides the chips; the desktop
                        popup has no draft state. */}
                    {!(isMobile && query !== '') && (
                      <div className="mt-auto flex flex-col items-start gap-l">
                        <p className="text-body-md font-medium text-gray">
                          {scope?.kind === 'market' ?
                            'Might be interesting'
                          : 'Popular questions'}
                        </p>
                        {chips.map(({ question }) => (
                          <button
                            key={question}
                            type="button"
                            className={CHIP_CLASS}
                            onClick={() =>
                              askQuestion(
                                question,
                                scope === null ? 'popular_question' : (
                                  'suggested_question'
                                ),
                              )
                            }
                          >
                            {question}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                : <AgentThread
                    messages={messages}
                    busy={busy}
                    failure={failure}
                    now={now}
                    surface="widget"
                    pair={pair}
                    onEdit={startEditing}
                    onRetrySend={(question) => askQuestion(question, 'retry')}
                    onRetryReply={reply}
                    onRetryLoad={retryLoad}
                  />
                }
              </div>
              {/* Hidden at the bottom so the last line is never veiled. */}
              <div
                aria-hidden
                hidden={!canScrollDown}
                className="pointer-events-none absolute inset-x-0 bottom-0 h-8.75 bg-linear-to-b from-transparent to-background-secondary"
              />
            </div>
            <div className="flex shrink-0 flex-col gap-s">
              {editing !== null && <AgentEditingBar onCancel={cancelEditing} />}
              {form}
            </div>
          </>
        }
      </div>
      {editing?.confirm === true && (
        <AgentEditConfirmModal onKeep={keepEditing} onConfirm={confirmEdit} />
      )}
    </>
  );
}

function onFinish(finish: AgentFinish) {
  track(
    'ai_widget_answer_finished',
    finish.tokens === null ?
      { status: finish.status }
    : { status: finish.status, tokens: finish.tokens },
  );
}
